import Image from "next/image";
import { BRANDS } from "@/enums/brands";

interface BrandImageProps {
  brand: BRANDS;
  className?: string;
  width?: number;
  height?: number;
  variant?: "small";
}

// Default dimensions for most brands
const defaultWidth = 115;
const defaultHeight = 46;
const defaultAspectRatio = defaultWidth / defaultHeight;

const BrandImage: React.FC<BrandImageProps> = ({
  brand,
  className,
  width,
  height,
  variant,
}) => {
  let src: string;
  let alt: string;
  let style: React.CSSProperties | undefined = undefined;
  let brandAspectRatio = defaultAspectRatio; // Start with default aspect ratio
  let brandDefaultWidth = defaultWidth; // Start with default width
  let brandDefaultHeight = defaultHeight; // Start with default height

  switch (brand) {
    case BRANDS.SEBAS:
      // SEBAS specific dimensions and ratios
      const sebasCombinedDefaultWidth = 159; // Combined width of mascot and text
      const sebasDefaultHeight = 46;
      brandAspectRatio = sebasCombinedDefaultWidth / sebasDefaultHeight;
      brandDefaultWidth = sebasCombinedDefaultWidth;
      brandDefaultHeight = sebasDefaultHeight;
      src = "/icons/SebasMascot.png"; // Dummy value, not used directly
      alt = "Sebanda Insurance Logo"; // Dummy value, not used directly
      break;
    case BRANDS.DEL_TORO:
      src = "/icons/delToro.png";
      alt = "Del Toro Logo";
      break;
    case BRANDS.MARIA:
      src = "/icons/Maria.png";
      alt = "MARIA Logo";
      style = { filter: "drop-shadow(2px 4px 6px black)" }; // Specific style
      break;
    case BRANDS.ROCKET:
      src = "/icons/rocket.png";
      alt = "Rocket Companies Logo";
      break;
    case BRANDS.TELEFONICA:
      src = "/icons/Telefonica.svg";
      alt = "Telefónica Logo";
      break;
    case BRANDS.MOVISTAR:
      src = "/icons/Movistar.png";
      alt = "Movistar Logo";
      // Define Movistar specific defaults and aspect ratio
      brandDefaultWidth = 62; // Specific default width override
      brandAspectRatio = 62 / 46;
      brandDefaultHeight = Math.round(brandDefaultWidth / brandAspectRatio); // Calculate default height based on aspect ratio
      break;
    case BRANDS.BANCO_AZTECA:
      src = "/icons/BancoAzteca.svg";
      alt = "Banco Azteca Logo";
      break;
    case BRANDS.ARNALDO:
      src = "/icons/arnaldo.svg";
      alt = "Arny's Logo";
      break;
    case BRANDS.RYDER:
      src = "/icons/Ryder.png";
      alt = "Ryder Logo";
      break;
    case BRANDS.SYSTEMGROUP:
      src = "/icons/Systemgroup.png";
      alt = "Systemgroup Logo";
      break;
    case BRANDS.CLICK_LOOP:
      src = "/icons/ClickLoop.png";
      alt = "Click Loop Logo";
      break;
    case BRANDS.DESOL:
      src = "/icons/desol.svg";
      alt = "DESOL Logo";
      break;
    default: // GAIL
      if (variant === "small") {
        brandDefaultWidth = 40;
        brandDefaultHeight = 40;
        brandAspectRatio = 1; // Square aspect ratio
        src = "/icons/gail2-icon-black.svg";
        alt = "GAIL Logo";
      } else {
        src = "/icons/gail2-logo.svg";
        alt = "GAIL Logo";
      }
      break;
  }

  // Calculate final dimensions based on props and brand's aspect ratio/defaults
  let widthSet: number;
  let heightSet: number;

  if (width !== undefined && height === undefined) {
    // Width provided, calculate height based on brand's aspect ratio
    widthSet = width;
    heightSet = Math.round(width / brandAspectRatio);
  } else if (height !== undefined && width === undefined) {
    // Height provided, calculate width based on brand's aspect ratio
    heightSet = height;
    widthSet = Math.round(height * brandAspectRatio);
  } else {
    // Use provided width and height, or the brand's defaults if none are provided
    widthSet = width ?? brandDefaultWidth;
    heightSet = height ?? brandDefaultHeight;
  }

  if (brand === BRANDS.SEBAS) {
    const sebasMascotDefaultWidth = 44;
    const sebasTextDefaultWidth = 115;
    const sebasMascotAspectRatio = sebasMascotDefaultWidth / brandDefaultHeight;
    const sebasTextAspectRatio = sebasTextDefaultWidth / brandDefaultHeight;
    const mascotWidth = Math.round(heightSet * sebasMascotAspectRatio);
    const textWidth = Math.round(heightSet * sebasTextAspectRatio);

    return (
      <div
        className={`${className || ""}`}
        style={{ height: `${heightSet}px` }}
      >
        <Image
          src="/icons/SebasMascot.png"
          alt="Sebanda Insurance Mascot"
          width={mascotWidth}
          height={heightSet}
          priority={true}
          className="mr-2" // Keep margin between images
        />
        <Image
          src="/icons/Sebas.png"
          alt="Sebanda Insurance Logo"
          width={textWidth}
          height={heightSet}
          priority={true}
        />
      </div>
    );
  }

  return (
    <Image
      src={src}
      alt={alt}
      width={widthSet}
      height={heightSet}
      priority={true}
      style={style}
      className={className}
    />
  );
};

export default BrandImage;
